import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface ProgressOption {
  id: number | null;
  name: string;
  completed?: number;
  total?: number;
}

/** Select-only combobox: focus stays on the trigger while arrows explore the listbox. */
export function ProgressPicker({ label, groups, selectedId, onSelect }: {
  label: string;
  groups: { label: string | null; options: ProgressOption[] }[];
  selectedId: number | null;
  onSelect: (id: number | null) => void;
}) {
  const listId = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const search = useRef({ text: '', time: 0 });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [position, setPosition] = useState({ left: 8, top: 0, width: 360, maxHeight: 320 });
  const options: ProgressOption[] = [{ id: null, name: 'All' }, ...groups.flatMap((group) => group.options)];
  const selectedIndex = Math.max(0, options.findIndex((option) => option.id === selectedId));
  const selected = options[selectedIndex]!;
  const activeIndex = Math.min(active, options.length - 1);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const bounds = trigger.current!.getBoundingClientRect();
      // The document's interface zoom also scales fixed-position portal coordinates.
      const inverse = Number(getComputedStyle(document.documentElement).getPropertyValue('--ui-zoom-inverse')) || 1;
      const viewportWidth = window.innerWidth * inverse;
      const viewportHeight = window.innerHeight * inverse;
      const width = Math.min(360, viewportWidth - 16);
      const top = Math.max(8, Math.min(bounds.bottom * inverse + 6, viewportHeight - 16));
      setPosition({ left: Math.max(8, Math.min(bounds.left * inverse, viewportWidth - width - 8)), top, width,
        maxHeight: Math.max(0, Math.min(320, viewportHeight - top - 8)) });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !trigger.current?.contains(event.target) && !menu.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);

  useEffect(() => {
    if (open) document.getElementById(`${listId}-${activeIndex}`)?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex, listId]);

  function choose(option: ProgressOption) {
    onSelect(option.id);
    setOpen(false);
    trigger.current?.focus();
  }

  function row(option: ProgressOption, index: number) {
    return <div key={option.id ?? 'all'} id={`${listId}-${index}`} role="option"
      aria-selected={option.id === selectedId} className={`progress-option${index === activeIndex ? ' is-active' : ''}`}
      onMouseDown={(event) => event.preventDefault()} onClick={() => choose(option)}>
      <span className="progress-name" title={option.name}>{option.name}</span>
      <span className="progress-count">{option.total === undefined ? '' : `${option.completed}/${option.total}`}</span>
    </div>;
  }

  let offset = 1;
  return <>
    <button ref={trigger} type="button" role="combobox" className="picker topbar-filter"
      aria-label={label} aria-haspopup="listbox" aria-expanded={open} aria-controls={open ? listId : undefined}
      aria-activedescendant={open ? `${listId}-${activeIndex}` : undefined}
      onBlur={() => setOpen(false)}
      onClick={() => { setActive(selectedIndex); search.current.text = ''; setOpen(!open); }}
      onKeyDown={(event) => {
        const key = event.key;
        if (key === 'Escape' || key === 'Tab') { setOpen(false); if (key === 'Escape') event.preventDefault(); return; }
        if (key === 'Enter' || key === ' ') {
          event.preventDefault();
          if (open) choose(options[activeIndex]!);
          else { setActive(selectedIndex); setOpen(true); }
        } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(key)) {
          event.preventDefault();
          const from = open ? activeIndex : selectedIndex;
          setActive(key === 'Home' ? 0 : key === 'End' ? options.length - 1
            : Math.max(0, Math.min(options.length - 1, from + (key === 'ArrowDown' ? 1 : -1))));
          setOpen(true);
        } else if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
          event.preventDefault();
          const now = Date.now();
          search.current = { text: now - search.current.time > 700 ? key : search.current.text + key, time: now };
          const found = options.findIndex((option) => option.name.toLocaleLowerCase().startsWith(search.current.text.toLocaleLowerCase()));
          if (found >= 0) setActive(found);
          setOpen(true);
        }
      }}>
      <span className="topbar-filter-label">{label}</span>
      <span className="progress-name progress-selected" title={selected.name}>{selected.name}</span>
      <span aria-hidden>▾</span>
    </button>
    {open ? createPortal(<div ref={menu} id={listId} role="listbox" aria-label={label}
      className="progress-menu" style={position}>
      {row(options[0]!, 0)}
      {groups.map((group, index) => {
        const start = offset;
        offset += group.options.length;
        const rows = group.options.map((option, i) => row(option, start + i));
        return group.label === null ? <div key={index}>{rows}</div>
          : <div key={index} role="group" aria-label={group.label}>
            <div className="progress-heading" title={group.label}>{group.label}</div>{rows}
          </div>;
      })}
    </div>, document.body) : null}
  </>;
}
